import type { useSessionNavigation } from "./use-session-navigation";
import type { useSessionPolling } from "./use-session-polling";
import type { SessionProviderPlatform } from "./use-session-provider-platform";
import type { SessionProviderStateBundle } from "./use-session-provider-state-bundle";
import type { SessionProviderLiveWiring } from "./use-session-provider-live-wiring";
import type { SessionProviderOverviewWiring } from "./use-session-provider-overview-wiring";
import { useSessionInitialLoad } from "./use-session-initial-load";

type SessionInitialLoadInput = Parameters<typeof useSessionInitialLoad>[0];
type SessionNavigationWiring = ReturnType<typeof useSessionNavigation>;
type SessionPollingWiring = ReturnType<typeof useSessionPolling>;

type InitialLoadCoreInput = Pick<
  SessionInitialLoadInput,
  | "api"
  | "router"
  | "sessionId"
  | "authLoading"
  | "userId"
  | "sessionGenerationRef"
  | "rootProductIdRef"
  | "timestamps"
  | "haltStatusDebugLog"
>;

type InitialLoadFlowInput = Pick<
  SessionInitialLoadInput,
  | "applySessionFromSource"
  | "restoreInitialNavigation"
  | "startPolling"
  | "clearPolling"
  | "cleanupLiveSignalTimers"
  | "resetLiveSignals"
  | "resetReadTracking"
  | "resetTimestamps"
  | "showUnauthorizedSessionModal"
  | "openInitialLoadGate"
  | "mergeProductReadItems"
  | "replaceProductsSeen"
>;

type InitialLoadMetaSetters = Pick<
  SessionInitialLoadInput,
  | "setSessionLoading"
  | "setNotFound"
  | "setTeamDefaultStartingRounds"
  | "setTeamDetails"
  | "setUrlRootProductId"
>;

type InitialLoadEntitySetters = Pick<
  SessionInitialLoadInput,
  | "setProducts"
  | "setVersions"
  | "setProductDetailsById"
  | "setVersionDetailsById"
  | "setMembers"
  | "setVersionPreviews"
  | "setCollabPreviews"
  | "setReflectionPreviews"
  | "setUserShortIdViews"
>;

type InitialLoadConversationSetters = Pick<
  SessionInitialLoadInput,
  | "setChatMessages"
  | "setCollabs"
  | "setChatMessageIds"
  | "setChatCursor"
  | "setChatHasMore"
  | "setChatLoadingMore"
  | "setCollabsByProduct"
>;

export interface SessionProviderInitialLoadWiringInput {
  platform: SessionProviderPlatform;
  sessionId: string;
  state: SessionProviderStateBundle;
  liveWiring: SessionProviderLiveWiring;
  overviewWiring: SessionProviderOverviewWiring;
  navigation: SessionNavigationWiring;
  polling: SessionPollingWiring;
}

export function useSessionProviderInitialLoadWiring(
  input: SessionProviderInitialLoadWiringInput,
): void {
  useSessionInitialLoad({
    ...selectInitialLoadCoreInput(input),
    ...selectInitialLoadFlowInput(input),
    ...selectInitialLoadMetaSetters(input.state),
    ...selectInitialLoadEntitySetters(input.state),
    ...selectInitialLoadConversationSetters(input.state),
  });
}

function selectInitialLoadCoreInput(
  input: SessionProviderInitialLoadWiringInput,
): InitialLoadCoreInput {
  return {
    api: input.platform.api,
    router: input.platform.router,
    sessionId: input.sessionId,
    authLoading: input.platform.authLoading,
    userId: input.platform.userId,
    sessionGenerationRef: input.state.refs.sessionGenerationRef,
    rootProductIdRef: input.state.refs.rootProductIdRef,
    timestamps: input.liveWiring.timestamps.timestamps,
    haltStatusDebugLog: input.platform.haltStatusDebugLog,
  };
}

function selectInitialLoadFlowInput(
  input: SessionProviderInitialLoadWiringInput,
): InitialLoadFlowInput {
  return {
    applySessionFromSource: input.overviewWiring.overview.applySessionFromSource,
    restoreInitialNavigation: input.navigation.restoreInitialNavigation,
    startPolling: input.polling.startPolling,
    clearPolling: input.polling.clearPolling,
    cleanupLiveSignalTimers: input.liveWiring.liveSignals.cleanupLiveSignalTimers,
    resetLiveSignals: input.liveWiring.liveSignals.resetLiveSignals,
    resetReadTracking: input.liveWiring.readTracking.resetReadTracking,
    resetTimestamps: input.liveWiring.timestamps.resetTimestamps,
    showUnauthorizedSessionModal:
      input.overviewWiring.accessModals.showUnauthorizedSessionModal,
    openInitialLoadGate: input.liveWiring.liveSignals.openInitialLoadGate,
    mergeProductReadItems: input.liveWiring.readTracking.mergeProductReadItems,
    replaceProductsSeen: input.liveWiring.readTracking.replaceProductsSeen,
  };
}

function selectInitialLoadMetaSetters(
  state: SessionProviderStateBundle,
): InitialLoadMetaSetters {
  return {
    setSessionLoading: state.meta.setLoading,
    setNotFound: state.meta.setNotFound,
    setTeamDefaultStartingRounds: state.meta.setTeamDefaultStartingRounds,
    setTeamDetails: state.meta.setTeamDetails,
    setUrlRootProductId: state.meta.setUrlRootProductId,
  };
}

function selectInitialLoadEntitySetters(
  state: SessionProviderStateBundle,
): InitialLoadEntitySetters {
  return {
    setProducts: state.entity.setProducts,
    setVersions: state.entity.setVersions,
    setProductDetailsById: state.entity.setProductDetailsById,
    setVersionDetailsById: state.entity.setVersionDetailsById,
    setMembers: state.entity.setMembers,
    setVersionPreviews: state.entity.setVersionPreviews,
    setCollabPreviews: state.entity.setCollabPreviews,
    setReflectionPreviews: state.entity.setReflectionPreviews,
    setUserShortIdViews: state.entity.setUserShortIdViews,
  };
}

function selectInitialLoadConversationSetters(
  state: SessionProviderStateBundle,
): InitialLoadConversationSetters {
  return {
    setChatMessages: state.conversation.setChatMessages,
    setCollabs: state.conversation.setCollabs,
    setChatMessageIds: state.conversation.setChatMessageIds,
    setChatCursor: state.conversation.setChatCursor,
    setChatHasMore: state.conversation.setChatHasMore,
    setChatLoadingMore: state.conversation.setChatLoadingMore,
    setCollabsByProduct: state.conversation.setCollabsByProduct,
  };
}
